import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { NotificationsBell } from "@/components/NotificationsBell";
import { WakeListener } from "@/components/WakeListener";
import { HealthVoiceReminder } from "@/components/HealthVoiceReminder";
import { InstallPWA } from "@/components/InstallPWA";
import { EnableNotifications } from "@/components/EnableNotifications";
import {
  LayoutDashboard, MessageSquare, Boxes, Car, FileText, ShieldCheck,
  Search, Lock, Building2, LogOut, Menu, X, Mail, Users, HeartPulse, Gift, Sparkles,
} from "lucide-react";

const NAV = [
  { to: "/app", end: true, icon: LayoutDashboard, label: "Brief quotidien" },
  { to: "/app/assistant", icon: MessageSquare, label: "Assistant OMY" },
  { to: "/app/memoire", icon: Boxes, label: "Mes biens" },
  { to: "/app/vehicules", icon: Car, label: "Véhicules" },
  { to: "/app/documents", icon: FileText, label: "Documents" },
  { to: "/app/inbox", icon: Mail, label: "OMY Inbox" },
  { to: "/app/garanties", icon: ShieldCheck, label: "Garanties" },
  { to: "/app/sante", icon: HeartPulse, label: "Santé" },
  { to: "/app/foyer", icon: Users, label: "Foyer" },
  { to: "/app/cadeaux", icon: Gift, label: "Cadeaux" },
  { to: "/app/recherche", icon: Search, label: "Recherche" },
  { to: "/app/confidentialite", icon: Lock, label: "Confidentialité" },
  { to: "/app/business", icon: Building2, label: "OMY Business" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => { logout(); navigate("/"); };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-5 flex items-center justify-between"><button onClick={() => navigate("/")} data-testid="logo-home-btn" aria-label="Retour à l'accueil"><Logo /></button><NotificationsBell /></div>
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-white/5"
              }`}>
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/5">
        <div className="px-3 pb-2 space-y-2">
          <EnableNotifications className="w-full" />
          <InstallPWA className="w-full" label="Installer l'app" />
        </div>
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium">
            {(user?.name || "?")[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
          <button onClick={doLogout} data-testid="logout-btn" className="text-muted-foreground hover:text-destructive">
            <LogOut size={18} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-white/5 omy-glass sticky top-0 h-screen">
        <SidebarContent />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 omy-glass h-full"><SidebarContent /></div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="lg:hidden sticky top-0 z-40 omy-glass border-b border-white/5 h-14 flex items-center justify-between px-4">
          <button onClick={() => navigate("/")} data-testid="logo-home-btn-mobile" aria-label="Retour à l'accueil"><Logo size={24} /></button>
          <div className="flex items-center gap-3">
            <NotificationsBell />
            <button data-testid="mobile-menu-btn" aria-label="Menu" onClick={() => setOpen(!open)} className="pl-1 text-foreground/80 hover:text-foreground">{open ? <X /> : <Menu />}</button>
          </div>
        </div>
        <main className="p-5 sm:p-8 max-w-6xl mx-auto"><Outlet /></main>
      </div>
      <WakeListener />
      <HealthVoiceReminder />
    </div>
  );
}
